import { humanize } from '@ea/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Paperclip, Upload } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { api, qs, type Page } from '@/lib/api';
import { formatDateTime, relativeTime } from '@/lib/format';
import { Badge, Button, Input, Select, type Tone } from './ui';

// ---------------------------------------------------------------------------
// Status badges
// ---------------------------------------------------------------------------
const STATUS_TONES: Record<string, Tone> = {
  // shipments
  request_received: 'neutral',
  pending_creation: 'neutral',
  awaiting_representative: 'warning',
  label_created: 'info',
  in_transit: 'info',
  delivery_exception: 'danger',
  delivered_to_representative: 'violet',
  awaiting_forwarding: 'warning',
  forwarded_to_client: 'info',
  delivered_to_client: 'success',
  closed: 'neutral',
  cancelled: 'neutral',
  // legs
  not_started: 'neutral',
  pre_transit: 'info',
  out_for_delivery: 'primary',
  delivered: 'success',
  failed_attempt: 'danger',
  exception: 'danger',
  returned: 'danger',
  lost: 'danger',
  unknown: 'neutral',
  // tasks
  in_progress: 'info',
  waiting_external: 'warning',
  blocked: 'danger',
  under_review: 'violet',
  completed: 'success',
  // tickets
  open: 'info',
  waiting_representative: 'warning',
  waiting_courier: 'warning',
  waiting_client: 'warning',
  pending_management: 'violet',
  resolved: 'success',
  reopened: 'danger',
  // payments
  draft: 'neutral',
  submitted: 'info',
  under_review_payment: 'violet',
  correction_requested: 'warning',
  verified: 'violet',
  awaiting_approval: 'warning',
  // settlements waiting for the owner's final approval
  owner_review: 'warning',
  approved: 'success',
  rejected: 'danger',
  awaiting_payment: 'primary',
  paid: 'success',
  disputed: 'danger',
  // contracts
  sent_for_signature: 'warning',
  partially_signed: 'warning',
  executed: 'success',
  expired: 'danger',
  terminated: 'neutral',
  // addresses / reps / users
  available: 'success',
  near_capacity: 'warning',
  full: 'danger',
  temporarily_unavailable: 'warning',
  inactive: 'neutral',
  active: 'success',
  onboarding: 'info',
  paused: 'warning',
  invited: 'info',
  suspended: 'danger',
  // bank calls
  requested: 'violet',
  scheduled: 'info',
  no_answer: 'warning',
  follow_up_required: 'warning',
  escalated: 'danger',
  // tracking registration
  pending: 'neutral',
  registered: 'success',
  failed: 'danger',
  not_configured: 'warning',
  // reports
  reviewed: 'success',
};

export function StatusBadge({ status, label }: { status: string | null | undefined; label?: string }) {
  if (!status) return <span className="text-muted">—</span>;
  return (
    <Badge tone={STATUS_TONES[status] ?? 'neutral'} dot>
      {label ?? humanize(status)}
    </Badge>
  );
}

export function PriorityBadge({ priority }: { priority: string }) {
  const tone: Record<string, Tone> = { low: 'neutral', normal: 'info', high: 'warning', critical: 'danger' };
  return <Badge tone={tone[priority] ?? 'neutral'}>{humanize(priority)}</Badge>;
}

export function LevelBadge({ level }: { level: number }) {
  const tone: Tone[] = ['neutral', 'info', 'warning', 'danger', 'violet'];
  const name = ['', 'L1 · Coordinator', 'L2 · Manager', 'L3 · Supervisor', 'L4 · Executive'];
  return <Badge tone={tone[level] ?? 'neutral'}>{name[level] ?? `L${level}`}</Badge>;
}

export function FreshnessBadge({ freshness, at }: { freshness: 'fresh' | 'aging' | 'stale' | 'never'; at?: string | null }) {
  const map = { fresh: ['success', 'Fresh'], aging: ['warning', 'Aging'], stale: ['danger', 'Stale'], never: ['neutral', 'No events'] } as const;
  const [tone, label] = map[freshness];
  return (
    <span title={at ? `Last courier event ${formatDateTime(at)}` : 'No courier events yet'}>
      <Badge tone={tone}>{label}</Badge>
    </span>
  );
}

export function TimeAgo({ value }: { value: string | Date | null | undefined }) {
  if (!value) return <span className="text-muted">—</span>;
  return (
    <time dateTime={new Date(value).toISOString()} title={formatDateTime(value)} className="whitespace-nowrap">
      {relativeTime(value)}
    </time>
  );
}

// ---------------------------------------------------------------------------
// Pickers
// ---------------------------------------------------------------------------
export interface DirectoryPerson {
  id: string;
  fullName: string;
  title: string | null;
  roles: string[];
  assignable: boolean;
}

export function useDirectory() {
  return useQuery({ queryKey: ['directory'], queryFn: () => api.get<DirectoryPerson[]>('/api/directory'), staleTime: 5 * 60_000 });
}

export function EmployeeSelect({ value, onChange, placeholder = 'Select employee', assignableOnly = true, allowEmpty, id, roles }: { value: string | null | undefined; onChange: (v: string) => void; placeholder?: string; assignableOnly?: boolean; allowEmpty?: boolean; id?: string; roles?: string[] }) {
  const { data } = useDirectory();
  const people = (data ?? []).filter((p) => (!assignableOnly || p.assignable) && (!roles || p.roles.some((r) => roles.includes(r))));
  return (
    <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled={!allowEmpty}>
        {placeholder}
      </option>
      {people.map((p) => (
        <option key={p.id} value={p.id}>
          {p.fullName}
          {p.title ? ` — ${p.title}` : ''}
        </option>
      ))}
    </Select>
  );
}

/** Select backed by a paginated list endpoint (clients, representatives, addresses...). */
/**
 * Dropdown of records from a list endpoint. Lists are capped (200), so when there are more records — or once someone
 * types — a search box queries the server; the chosen record stays shown even when it is outside the loaded page.
 */
export function RecordSelect({ endpoint, value, onChange, label, placeholder, allowEmpty = true, params, id }: { endpoint: string; value: string | null | undefined; onChange: (v: string) => void; label: (r: any) => string; placeholder: string; allowEmpty?: boolean; params?: Record<string, string>; id?: string }) {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const key = endpoint.replace('/api/', '');
  const { data, isLoading } = useQuery({
    queryKey: [key, 'options', params, search],
    queryFn: () => api.get<Page<any>>(`${endpoint}${qs({ pageSize: 200, ...params, q: search || undefined })}`),
    placeholderData: keepPreviousData,
  });
  const items = data?.items ?? [];
  const missing = !!value && !items.some((r) => r.id === value);
  const selected = useQuery({ queryKey: [key, 'one', value], queryFn: () => api.get<any>(`${endpoint}/${value}`), enabled: missing, retry: false, staleTime: 300_000 });
  const more = (data?.total ?? 0) > items.length;
  return (
    <div className="space-y-1.5">
      {(more || search) && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search…" aria-label={`Search ${placeholder.toLowerCase()}`} />}
      <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={isLoading && !data}>
        <option value="" disabled={!allowEmpty}>
          {isLoading && !data ? 'Loading…' : placeholder}
        </option>
        {missing && selected.data && <option value={value!}>{label(selected.data)}</option>}
        {items.map((r) => (
          <option key={r.id} value={r.id}>
            {label(r)}
          </option>
        ))}
      </Select>
      {more && <p className="text-xs text-muted">{`Showing ${items.length} of ${data!.total}${search ? ' matches' : ''} — type a name to find others.`}</p>}
    </div>
  );
}

export function EnumSelect({ values, value, onChange, placeholder, id, allowEmpty = true }: { values: readonly string[]; value: string | undefined | null; onChange: (v: string) => void; placeholder?: string; id?: string; allowEmpty?: boolean }) {
  return (
    <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      {placeholder !== undefined && (
        <option value="" disabled={!allowEmpty}>
          {placeholder}
        </option>
      )}
      {values.map((v) => (
        <option key={v} value={v}>
          {humanize(v)}
        </option>
      ))}
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------
export interface AttachmentRow {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  category: string | null;
  createdAt: string;
  uploadedByName: string | null;
}

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.txt,.csv,.eml,.docx,.xlsx';

export function Attachments({ entityType, entityId, items, invalidate, category, categories, canUpload = true }: { entityType: string; entityId: string; items: AttachmentRow[]; invalidate: unknown[]; category?: string; categories?: { value: string; label: string }[]; canUpload?: boolean }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const catRef = useRef<HTMLSelectElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.upload(`/api/attachments${qs({ entityType, entityId, category: catRef.current?.value || category })}`, file),
    onSuccess: () => {
      toast.success('File uploaded');
      qc.invalidateQueries({ queryKey: invalidate });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-muted">No files attached.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {items.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <Paperclip className="size-4 shrink-0 text-muted" />
                <div className="min-w-0">
                  <p className="truncate font-medium">{a.fileName}</p>
                  <p className="text-xs text-muted">
                    {a.category ? `${humanize(a.category)} · ` : ''}
                    {(a.sizeBytes / 1024).toFixed(0)} KB · {a.uploadedByName ?? 'Unknown'} · {formatDateTime(a.createdAt)}
                  </p>
                </div>
              </div>
              <a href={`/api/attachments/${a.id}/download`} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-primary-soft">
                <Download className="size-3.5" /> Download
              </a>
            </li>
          ))}
        </ul>
      )}
      {canUpload && (
        <div className="flex flex-wrap items-center gap-2">
          {categories && (
            <Select ref={catRef} className="w-auto" defaultValue={categories[0]?.value}>
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
          />
          <Button size="sm" icon={<Upload className="size-4" />} loading={upload.isPending} onClick={() => input.current?.click()}>
            Attach file
          </Button>
          <span className="text-xs text-muted">PDF, images, text, Word or Excel · max 15 MB</span>
        </div>
      )}
    </div>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return <span className="text-muted">{children}</span>;
}
